import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import type { WidgetCardDragHandleProps } from "./WidgetCard"

/**
 * Studio-agnostic widget-registry shape. Each studio keeps its own registry
 * INSTANCE (widget list + copy + links are studio-specific), but the type is
 * shared so the board composition (this file) and CustomizeTray can consume
 * either registry without a cast.
 */
export type DashboardTranslateFn = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

export interface DashboardWidgetDef<Stats> {
  titleKey: TranslationKeys
  wide?: boolean
  linkTo?: string
  linkLabelKey?: TranslationKeys
  // `locale` (e.g. "en-US"/"de-DE", see localeForLanguage) lets
  // implementations wrap any raw numbers through formatInt so header counts
  // render with locale-correct thousands separators, matching every other
  // number on the board instead of a bare `toString()`.
  countText?: (
    stats: Stats,
    translate: DashboardTranslateFn,
    locale: string,
  ) => string
  // `loadFailed`: the dashboard did not load, so `stats` may be the zero fallback
  // (an empty list then means "unknown", not "none").
  component: React.ComponentType<{ stats: Stats; loadFailed: boolean }>
}

export interface SortableWidgetProps {
  id: string
  disabled: boolean
  wide?: boolean
  children: (state: {
    isDragging: boolean
    isOver: boolean
    dragHandleProps: WidgetCardDragHandleProps
  }) => React.ReactNode
}

/**
 * Drag-sortable wrapper around a single board widget. Extracted unchanged
 * from the supplier DashboardPage — pure dnd-kit plumbing, no studio-specific
 * coupling (stats shape, registry, store) ever lived here.
 */
export function SortableWidget({
  id,
  disabled,
  wide,
  children,
}: SortableWidgetProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id, disabled })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(wide && "col-span-full")}
    >
      {children({
        isDragging,
        isOver: isOver && !isDragging,
        dragHandleProps: { attributes, listeners, setActivatorNodeRef },
      })}
    </div>
  )
}
